import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { payrollIntegrationService } from '../../../services/payrollIntegrationService';

const EmployeeSyncTable = ({ connection }) => {
  const [syncRecords, setSyncRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (connection?.id) {
      loadSyncRecords();
    }
  }, [connection]);

  const loadSyncRecords = async () => {
    try {
      setLoading(true);
      const sessions = await payrollIntegrationService?.getAllIntegrationSessions();
      if (sessions?.length > 0) {
        const latestSession = sessions?.[0];
        const records = await payrollIntegrationService?.getIntegrationRecordsBySession(latestSession?.id);
        setSyncRecords(records || []);
      }
    } catch (error) {
      console.error('Error loading sync records:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = filter === 'all' 
    ? syncRecords 
    : syncRecords?.filter(r => r?.validationStatus === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Employee Synchronization</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time processing status with individual record validation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-lg text-foreground"
          >
            <option value="all">All Records</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="warning">Warnings</option>
          </select>
          <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2">
            <Icon name="RefreshCw" size={16} />
            Sync Now
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading employee sync records...</p>
        </div>
      ) : (
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Gross Pay (PGK)</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Net Pay (PGK)</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords?.map((record) => (
                  <tr key={record?.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-foreground">{record?.employeeName}</div>
                        <div className="text-xs text-muted-foreground">{record?.employeeNumber}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-foreground">{record?.department}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-medium text-foreground">
                        K {record?.grossPay?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-medium text-foreground">
                        K {record?.netPay?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        record?.validationStatus === 'passed' ? 'bg-success/10 text-success' :
                        record?.validationStatus === 'failed'? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                      }`}>
                        <Icon name={
                          record?.validationStatus === 'passed' ? 'CheckCircle' :
                          record?.validationStatus === 'failed'? 'XCircle' : 'AlertCircle'
                        } size={12} />
                        {record?.validationStatus}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button className="text-primary hover:text-primary/80">
                        <Icon name="Eye" size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSyncTable;
